import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
// import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Fade from '@mui/material/Fade';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
// import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
// import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import ArgonBox from 'components/ArgonBox';

import { useLanguage, FLAG_ES, FLAG_EN } from 'context/LanguageContext';
import { useUserData } from 'context/UserDataContext';
import { useAuth } from 'context/AuthContext';
import { apiRequest } from 'utils/api';
import { ACCENT } from 'layouts/authentication/constants/loginTheme';

const LANG_BASE = [
  { code: 'es', flagSrc: FLAG_ES, primaryLabel: 'Español', secondaryLabel: 'Spanish' },
  { code: 'en', flagSrc: FLAG_EN, primaryLabel: 'English', secondaryLabel: 'Inglés' },
];

function LangCard({ flagSrc, primaryLabel, secondaryLabel, active, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      disableRipple
      aria-pressed={active}
      aria-label={primaryLabel}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        px: 2.5,
        py: 2,
        borderRadius: '14px',
        border: '1.5px solid',
        borderColor: active ? ACCENT : 'rgba(0,0,0,0.08)',
        bgcolor: active ? 'rgba(30,134,246,0.06)' : '#ffffff',
        cursor: 'pointer',
        textAlign: 'left',
        position: 'relative',
        transition: 'all 0.2s ease',
        boxShadow: active
          ? `0 0 0 1px ${ACCENT}18, 0 2px 12px rgba(30,134,246,0.1)`
          : '0 1px 4px rgba(0,0,0,0.04)',
        '&:hover': {
          borderColor: active ? ACCENT : 'rgba(0,0,0,0.16)',
          bgcolor: active ? 'rgba(30,134,246,0.08)' : '#fafbfc',
          boxShadow: active
            ? `0 0 0 1px ${ACCENT}28, 0 4px 16px rgba(30,134,246,0.12)`
            : '0 2px 8px rgba(0,0,0,0.07)',
        },
        '&:active': { transform: 'scale(0.995)' },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          boxShadow: active
            ? `0 0 0 2px #fff, 0 0 0 3.5px ${ACCENT}`
            : '0 1px 5px rgba(0,0,0,0.12)',
          transition: 'box-shadow 0.2s ease',
        }}
      >
        <Box
          component="img"
          src={flagSrc}
          alt=""
          loading="eager"
          decoding="async"
          draggable={false}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            borderRadius: '50%',
            pointerEvents: 'none',
            filter: active ? 'none' : 'saturate(0.88) brightness(0.95)',
            transition: 'filter 0.2s ease',
          }}
        />
      </Box>

      <Box flexGrow={1} minWidth={0}>
        <Box
          component="p"
          sx={{
            m: 0,
            fontSize: '1.05rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.25,
            color: active ? ACCENT : '#1e293b',
            transition: 'color 0.2s ease',
          }}
        >
          {primaryLabel}
        </Box>
        <Box
          component="p"
          sx={{
            m: 0,
            mt: 0.25,
            fontSize: '0.8125rem',
            fontWeight: 400,
            lineHeight: 1.3,
            color: active ? 'rgba(30,134,246,0.72)' : '#94a3b8',
            transition: 'color 0.2s ease',
          }}
        >
          {secondaryLabel}
        </Box>
      </Box>

      <Fade in={active} timeout={180}>
        <CheckCircleRoundedIcon sx={{ fontSize: 22, color: ACCENT, flexShrink: 0 }} />
      </Fade>
    </ButtonBase>
  );
}

LangCard.propTypes = {
  flagSrc: PropTypes.string.isRequired,
  primaryLabel: PropTypes.string.isRequired,
  secondaryLabel: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

/** Resuelve el nombre visible del usuario desde distintas fuentes posibles */
function resolveDisplayName(userData, firebaseUser) {
  if (userData) {
    const fromNombreApellido = [userData.nombre, userData.apellido].filter(Boolean).join(' ').trim();
    if (fromNombreApellido) return fromNombreApellido;

    const fromPrimerNombre = [userData.primerNombre, userData.primerApellido || userData.apellido]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fromPrimerNombre) return fromPrimerNombre;

    if (typeof userData.nombre === 'string' && userData.nombre.trim()) {
      return userData.nombre.trim();
    }
  }

  if (firebaseUser?.displayName?.trim()) {
    return firebaseUser.displayName.trim();
  }

  const email = userData?.email || firebaseUser?.email || '';
  if (email.includes('@')) {
    const local = email.split('@')[0];
    return local
      .replace(/[._-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return '';
}

/** Busca la URL de foto en el objeto userData (múltiples keys posibles) */
function resolvePhotoUrl(userData) {
  if (!userData) return '';
  const keys = ['imagen', 'imagenUrl', 'foto', 'fotoPerfil', 'foto_perfil', 'avatar', 'avatarUrl', 'avatar_url', 'photoURL', 'photoUrl', 'profileImage', 'picture', 'image'];
  for (const k of keys) {
    const v = userData[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function Ajustes() {
  const { lang, setLanguage, t, ipLang } = useLanguage();
  const { userData, updateUserData, fetchUserData } = useUserData();
  const { user } = useAuth();
  // const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localPhoto, setLocalPhoto] = useState(null);
  const [photoError, setPhotoError] = useState(false);

  const LANG_OPTIONS = ipLang === 'en' ? [...LANG_BASE].reverse() : LANG_BASE;

  const displayName = resolveDisplayName(userData, user);
  const email = userData?.email || user?.email || '';

  // Prioridad: foto subida en esta sesión → userData → photoURL de Firebase
  const photoUrl = localPhoto || resolvePhotoUrl(userData) || user?.photoURL || '';

  const handlePhotoClick = () => {
    if (!uploadingPhoto) fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploadingPhoto(true);
    setPhotoError(false);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiRequest('/upload-image', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      const newPhotoUrl =
        data.url_original ||
        data.url_display ||
        data.imagen ||
        resolvePhotoUrl(data);

      if (!newPhotoUrl) throw new Error('No image URL in response');

      setLocalPhoto(newPhotoUrl);

      if (userData) {
        updateUserData({ ...userData, imagen: newPhotoUrl });
      }

      await fetchUserData(true);
    } catch {
      setPhotoError(true);
      setLocalPhoto(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <DashboardLayout>
      <ArgonBox py={3} sx={{ position: 'relative', zIndex: 1 }}>

        {/* ── Header: perfil del usuario ──────────────────── */}
        <Box
          display="flex"
          alignItems="center"
          gap={2.5}
          mb={3}
          sx={{ position: 'relative', zIndex: 2 }}
        >
          {/* Avatar clickeable */}
          <Tooltip title={t('settings.changePhoto')} placement="bottom">
            <Box
              onClick={handlePhotoClick}
              sx={{
                position: 'relative',
                cursor: uploadingPhoto ? 'default' : 'pointer',
                flexShrink: 0,
                '&:hover .avatar-overlay': { opacity: 1 },
              }}
            >
              <Avatar
                src={photoError ? undefined : photoUrl}
                alt={displayName}
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: '1.9rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  border: '3px solid rgba(255,255,255,0.35)',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.18)',
                  filter: uploadingPhoto ? 'brightness(0.6)' : 'none',
                  transition: 'filter 0.2s ease',
                }}
              >
                {displayName ? displayName.charAt(0).toUpperCase() : '?'}
              </Avatar>

              {/* Overlay cámara */}
              {!uploadingPhoto && (
                <Box
                  className="avatar-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.40)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.18s ease',
                  }}
                >
                  <CameraAltRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
              )}

              {/* Spinner de carga */}
              {uploadingPhoto && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={26} sx={{ color: '#fff' }} />
                </Box>
              )}
            </Box>
          </Tooltip>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {/* Nombre, email y subtítulo — HTML plano, sin componentes del template */}
          <Box sx={{ position: 'relative', zIndex: 2, minWidth: 0 }}>
            <Box
              component="p"
              sx={{
                m: 0,
                mb: 0.4,
                color: '#ffffff',
                fontSize: { xs: '1.35rem', sm: '1.65rem' },
                fontWeight: 700,
                letterSpacing: '-0.025em',
                lineHeight: 1.2,
              }}
            >
              {displayName}
            </Box>
            {email && (
              <Box
                component="p"
                sx={{
                  m: 0,
                  mb: 0.35,
                  color: 'rgba(255,255,255,0.78)',
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                }}
              >
                {email}
              </Box>
            )}
            <Box
              component="p"
              sx={{
                m: 0,
                color: 'rgba(255,255,255,0.52)',
                fontSize: '0.875rem',
                lineHeight: 1.4,
              }}
            >
              {t('settings.subtitle')}
            </Box>
            {photoError && (
              <Box
                component="p"
                sx={{
                  m: 0,
                  mt: 0.5,
                  color: 'rgba(255,200,200,0.95)',
                  fontSize: '0.75rem',
                }}
              >
                {t('settings.photoError')}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Idioma ─────────────────────────────────────── */}
        <Card
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: '18px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
          }}
        >
          <Box mb={2.5}>
            <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 0.3 }}>
              {t('settings.languageSection')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings.languageDesc')}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1.25}>
            {LANG_OPTIONS.map(({ code, flagSrc, primaryLabel, secondaryLabel }) => (
              <LangCard
                key={code}
                flagSrc={flagSrc}
                primaryLabel={primaryLabel}
                secondaryLabel={secondaryLabel}
                active={lang === code}
                onClick={() => setLanguage(code)}
              />
            ))}
          </Box>
        </Card>

        {/* ── Historial de acciones (oculto temporalmente) ──
        <Card
          sx={{
            mt: 2,
            borderRadius: '18px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            overflow: 'hidden',
          }}
        >
          <ButtonBase
            onClick={() => navigate('/historial-acciones')}
            disableRipple
            sx={{
              width: '100%',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: { xs: 2.5, sm: 4 },
              py: 3,
              transition: 'background 0.18s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' },
              '&:active': { bgcolor: 'rgba(0,0,0,0.045)' },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(30,134,246,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HistoryRoundedIcon sx={{ color: ACCENT, fontSize: 22 }} />
            </Box>
            <Box flexGrow={1}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                {t('settings.actionHistory')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.actionHistoryDesc')}
              </Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
          </ButtonBase>
        </Card>
        */}

      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Ajustes;
